const IVORION_NAV_OPEN_EVENT = 'ivorion-navigation:open';
const IVORION_DESKTOP_HEADER_QUERY = window.matchMedia('(min-width: 1024px)');
const IVORION_REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
const IVORION_HOVER_OPEN_DELAY = 350;
const IVORION_COLLAPSE_SETTLE_DELAY = 650;

function syncIvorionStickyHeaderMode() {
  const mode = IVORION_DESKTOP_HEADER_QUERY.matches ? 'always' : 'scroll-up';

  document.querySelectorAll('sticky-header.header-wrapper').forEach((header) => {
    if (header.getAttribute('mode') === mode) return;

    header.setAttribute('mode', mode);

    if (typeof header.handleWindowScroll === 'function') {
      header.handleWindowScroll();
    }
  });
}

syncIvorionStickyHeaderMode();
IVORION_DESKTOP_HEADER_QUERY.addEventListener('change', syncIvorionStickyHeaderMode);
document.addEventListener('shopify:section:load', syncIvorionStickyHeaderMode);

class IvorionNavigationDropdown extends HTMLElement {
  constructor() {
    super();
    this.openTimer = null;
    this.closeTimer = null;
    this.positionFrame = null;
    this.revealFrame = null;
    this.revealTimer = null;
    this.revealObserver = null;
    this.initialized = false;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleOtherDropdownOpen = this.handleOtherDropdownOpen.bind(this);
    this.handleCompetingNavigationOpen = this.handleCompetingNavigationOpen.bind(this);
    this.handleCollapseOpen = this.handleCollapseOpen.bind(this);
    this.handleDesktopBreakpoint = this.handleDesktopBreakpoint.bind(this);
    this.schedulePosition = this.schedulePosition.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.trigger = this.querySelector('[data-ivorion-nav-trigger]');
    this.panel = this.querySelector('[data-ivorion-nav-panel]');

    if (!(this.trigger instanceof HTMLButtonElement) || !(this.panel instanceof HTMLElement)) return;

    this.initialized = true;
    this.openTrigger = this.dataset.openTrigger === 'hover' ? 'hover' : 'click';

    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.trigger.addEventListener('keydown', this.handleTriggerKeydown);
    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('collapse:open', this.handleCollapseOpen);

    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    document.addEventListener(IVORION_NAV_OPEN_EVENT, this.handleOtherDropdownOpen);
    document.addEventListener('mega-menu:open', this.handleCompetingNavigationOpen);
    document.addEventListener('search:open', this.handleCompetingNavigationOpen);
    document.addEventListener('sticky-header:update', this.schedulePosition);
    IVORION_DESKTOP_HEADER_QUERY.addEventListener('change', this.handleDesktopBreakpoint);
    window.addEventListener('resize', this.schedulePosition, { passive: true });
    window.addEventListener('scroll', this.schedulePosition, { passive: true });
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    window.cancelAnimationFrame(this.positionFrame);
    this.stopExpandedRevealTracking();

    this.trigger.removeEventListener('click', this.handleTriggerClick);
    this.trigger.removeEventListener('keydown', this.handleTriggerKeydown);
    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('collapse:open', this.handleCollapseOpen);

    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    document.removeEventListener(IVORION_NAV_OPEN_EVENT, this.handleOtherDropdownOpen);
    document.removeEventListener('mega-menu:open', this.handleCompetingNavigationOpen);
    document.removeEventListener('search:open', this.handleCompetingNavigationOpen);
    document.removeEventListener('sticky-header:update', this.schedulePosition);
    IVORION_DESKTOP_HEADER_QUERY.removeEventListener('change', this.handleDesktopBreakpoint);
    window.removeEventListener('resize', this.schedulePosition);
    window.removeEventListener('scroll', this.schedulePosition);

    this.initialized = false;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get canHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  handleTriggerClick() {
    window.clearTimeout(this.openTimer);

    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  handleTriggerKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    this.openMenu({ focus: event.key === 'ArrowUp' ? 'last' : 'first' });
  }

  handlePointerEnter() {
    if (this.openTrigger !== 'hover' || !this.canHover) return;

    window.clearTimeout(this.closeTimer);
    this.queueOpen();
  }

  handlePointerLeave() {
    if (this.openTrigger !== 'hover' || !this.canHover) return;

    window.clearTimeout(this.openTimer);
    this.queueClose();
  }

  handleFocusOut(event) {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.queueClose(0);
  }

  handleDocumentPointerDown(event) {
    if (!this.isOpen || !(event.target instanceof Node) || this.contains(event.target)) return;

    this.closeMenu();
  }

  handleDocumentKeydown(event) {
    if (!this.isOpen || event.key !== 'Escape') return;

    event.preventDefault();
    this.closeMenu({ returnFocus: true });
  }

  handleOtherDropdownOpen(event) {
    if (event.detail?.dropdown === this) return;

    this.closeMenu();
  }

  handleCompetingNavigationOpen() {
    this.closeMenu();
  }

  handleCollapseOpen(event) {
    const collapse = event.target;

    if (!(collapse instanceof HTMLElement) || !collapse.matches('.ivorion-nav__collapse') || !this.panel.contains(collapse)) {
      return;
    }

    this.trackExpandedCollapse(collapse);
  }

  handleDesktopBreakpoint(event) {
    if (!event.matches) {
      this.closeMenu();
    } else {
      this.schedulePosition();
    }
  }

  queueOpen(delay = IVORION_HOVER_OPEN_DELAY) {
    if (this.isOpen) return;

    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => {
      if (this.matches(':hover')) this.openMenu();
    }, delay);
  }

  queueClose(delay = 180) {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      if (this.matches(':hover') || this.contains(document.activeElement)) return;
      this.closeMenu();
    }, delay);
  }

  openMenu({ focus = null } = {}) {
    if (this.isOpen) {
      if (focus) this.focusPanelItem(focus);
      return;
    }

    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);

    document.dispatchEvent(
      new CustomEvent(IVORION_NAV_OPEN_EVENT, {
        detail: { dropdown: this },
      }),
    );

    const megaMenu = document.querySelector('mega-menu');
    if (megaMenu && 'open' in megaMenu) megaMenu.open = false;

    this.setAttribute('open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    this.schedulePosition();

    if (focus) {
      window.requestAnimationFrame(() => this.focusPanelItem(focus));
    }
  }

  closeMenu({ returnFocus = false } = {}) {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);

    if (!this.isOpen) return;

    this.stopExpandedRevealTracking();
    this.removeAttribute('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    this.panel.style.removeProperty('left');
    this.panel.style.removeProperty('top');
    this.panel.style.removeProperty('--ivorion-nav-panel-max-height');

    if (returnFocus) this.trigger.focus({ preventScroll: true });
  }

  focusPanelItem(position) {
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
      (element) => !element.hidden && element.getClientRects().length > 0,
    );

    const target = position === 'last' ? focusable.at(-1) : focusable[0];
    target?.focus({ preventScroll: true });

    if (target instanceof HTMLElement) {
      this.ensureElementVisible(target);
    }
  }

  trackExpandedCollapse(collapse) {
    this.stopExpandedRevealTracking();

    const reveal = () => {
      window.cancelAnimationFrame(this.revealFrame);
      this.revealFrame = window.requestAnimationFrame(() => {
        if (this.isOpen && collapse.hasAttribute('open')) {
          this.ensureElementVisible(collapse);
        }
      });
    };

    reveal();

    if ('ResizeObserver' in window) {
      this.revealObserver = new ResizeObserver(reveal);
      this.revealObserver.observe(collapse);
    }

    this.revealTimer = window.setTimeout(() => {
      this.stopExpandedRevealTracking();

      if (this.isOpen && collapse.hasAttribute('open')) {
        this.ensureElementVisible(collapse, {
          behavior: IVORION_REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth',
        });
      }
    }, IVORION_COLLAPSE_SETTLE_DELAY);
  }

  stopExpandedRevealTracking() {
    window.clearTimeout(this.revealTimer);
    window.cancelAnimationFrame(this.revealFrame);
    this.revealObserver?.disconnect();
    this.revealObserver = null;
  }

  ensureElementVisible(element, { behavior = 'auto' } = {}) {
    if (!this.isOpen || !(element instanceof HTMLElement)) return;

    const panelRect = this.panel.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const visibilityPadding = 12;
    const visibleTop = panelRect.top + visibilityPadding;
    const visibleBottom = panelRect.bottom - visibilityPadding;
    const availableHeight = Math.max(0, visibleBottom - visibleTop);
    let scrollDelta = 0;

    if (elementRect.height > availableHeight) {
      scrollDelta = elementRect.top - visibleTop;
    } else if (elementRect.bottom > visibleBottom) {
      scrollDelta = elementRect.bottom - visibleBottom;
    } else if (elementRect.top < visibleTop) {
      scrollDelta = elementRect.top - visibleTop;
    }

    if (Math.abs(scrollDelta) < 1) return;

    const maximumScroll = Math.max(0, this.panel.scrollHeight - this.panel.clientHeight);
    const nextScroll = Math.min(Math.max(this.panel.scrollTop + scrollDelta, 0), maximumScroll);

    this.panel.scrollTo({ top: nextScroll, behavior });
  }

  schedulePosition() {
    if (!this.isOpen) return;

    window.cancelAnimationFrame(this.positionFrame);
    this.positionFrame = window.requestAnimationFrame(() => this.positionPanel());
  }

  positionPanel() {
    if (!this.isOpen) return;

    const triggerRect = this.trigger.getBoundingClientRect();
    const panelRect = this.panel.getBoundingClientRect();
    const viewportPadding = 16;
    const isRtl = document.documentElement.dir === 'rtl';

    let left = isRtl ? triggerRect.right - panelRect.width : triggerRect.left;
    const maximumLeft = Math.max(viewportPadding, window.innerWidth - panelRect.width - viewportPadding);
    left = Math.min(Math.max(left, viewportPadding), maximumLeft);

    const top = Math.max(0, triggerRect.bottom);
    const availableHeight = Math.max(1, window.innerHeight - top - viewportPadding);

    this.panel.style.left = `${Math.round(left)}px`;
    this.panel.style.top = `${Math.round(top)}px`;
    this.panel.style.setProperty('--ivorion-nav-panel-max-height', `${Math.round(availableHeight)}px`);
  }
}

if (!customElements.get('ivorion-navigation-dropdown')) {
  customElements.define('ivorion-navigation-dropdown', IvorionNavigationDropdown);
}
